import {useState, useEffect, useMemo} from 'react'
import compose from 'recompose/compose'
import {useDispatch} from 'react-redux'
import {showLoginModal} from '@/redux/modules/layout/actions'
import {useHistory} from 'react-router-dom'
import {useMutation} from '@apollo/react-hooks'
import SIMPLIFIED_SITE_SELLER_LEAD_CREATE from '@/graphql/mutations/simplifiedSiteSellerLeadCreate'
import withUserProfile from '@/lib/hoc/withUserProfile'
import withRestoredScroll from '@/lib/hoc/withRestoredScroll'
import {getAddressInput} from '@/lib/address'
import {getUTMObject} from '@/lib/utm'
import {POSTALCODE_LENGTH} from '@/lib/format'
import logger from '@/lib/logger'
import slugify from 'slugify'
import {getSha256Data} from '@/lib/user'
import {MIN_PRICE} from '@/lib/values'
import HeadHelmet from '@/components/shared/HeadHelmet'
import Banner from '@/components/shared/Banner'
import Shell from '@/components/layout/templates/Default'
import StepByStep from '@/components/shared/StepByStep'
import {EMAIL_REGEX} from '@/components/form/EmailInput'
import Listing from '../../listing/ListingAddress'
import Contact from './steps/Contact'
import classNames from 'classnames'
import styles from '@/styles/templates/StepByStep/styles.module.css'

const INITIAL_STEP = 'listing'
const INITIAL_TYPE = 'Apartamento'
const MIN_COMPLEMENT_LENGTH = 1
const PHONE_LENGTH = 14
const loggerAction = {
  success: 'seller-onboarding-success'
}

const getStateValue = (state, key, d) => (state && state[key]) || d

const SellerCreatePage = ({userProfile, location: {state}}) => {
  const dispatch = useDispatch()
  const history = useHistory()
  const [simplifiedSiteSellerLeadCreate, {called, error, data}] = useMutation(
    SIMPLIFIED_SITE_SELLER_LEAD_CREATE
  )
  const [input, setInput] = useState()
  const [step, changeStep] = useState(INITIAL_STEP)
  const [stepStates, changeStepStates] = useState({
    listing: {
      types: getStateValue(state, 'types', [INITIAL_TYPE]),
      address: getStateValue(state, 'address', null),
      addressData: getStateValue(state, 'addressData', null),
      streetNumber: getStateValue(
        state,
        'streetNumber',
        state && state.streetNumberNotFound ? undefined : null
      ),
      streetNumberNotFound: getStateValue(state, 'streetNumberNotFound', null),
      postalCode: getStateValue(
        state,
        'postalCode',
        state && state.postalCodeNotFound ? undefined : null
      ),
      postalCodeNotFound: getStateValue(state, 'postalCodeNotFound', null),
      complement: getStateValue(state, 'complement', null),
      price: getStateValue(state, 'price', undefined)
    },
    contact: {
      name: undefined,
      phone: undefined,
      email: undefined
    }
  })
  const [result, changeResult] = useState({
    error: true,
    icon: '/img/icons/exclamation-mark.svg',
    title: 'Ocorreu um erro',
    text: 'Tente enviar os dados novamente',
    button: 'Tentar novamente',
    buttonOnClick: () => changeStep(INITIAL_STEP)
  })
  const steps = {
    listing: {
      label: 'Imóvel',
      component: Listing,
      stepBack: null,
      stepFwd: 'contact',
      stepProps: {
        state: stepStates.listing,
        onChange: (value) => {
          changeStepStates({
            ...stepStates,
            listing: value
          })
        },
        onSubmit: onSubmitSteps,
        loggerAction: {
          listTypes: 'seller-onboarding-step-listing-types',
          addressFocus: 'seller-onboarding-step-listing-address-focus',
          addressData: 'seller-onboarding-step-listing-address-data',
          listingComplement: 'seller-onboarding-step-listing-complement',
          streetNumber: 'seller-onboarding-step-listing-streetNumber',
          postalCode: 'seller-onboarding-step-listing-postalCode',
          listingPrice: 'seller-onboarding-step-listing-price',
          streetNumberNotFound:
            'seller-onboarding-step-listing-streetnumberotfounded',
          postalCodeNotFound:
            'seller-onboarding-step-listing-postalCodeNotfounded'
        },
        hasPrice: true
      }
    },
    contact: {
      label: 'Contato',
      component: Contact,
      stepBack: 'listing',
      stepFwd: 'send',
      stepProps: {
        state: stepStates.contact,
        userProfile,
        onChange: (value) => {
          changeStepStates({
            ...stepStates,
            contact: value
          })
        },
        onSubmit: onSubmitSteps
      }
    }
  }

  const isStepListingValid = () => {
    let isValid =
      stepStates['listing'].address !== null &&
      stepStates['listing'].addressData !== null &&
      stepStates['listing'].streetNumber !== null &&
      stepStates['listing'].streetNumber !== undefined &&
      stepStates['listing'].streetNumber.length !== 0 &&
      stepStates['listing'].postalCode !== null &&
      stepStates['listing'].postalCode !== undefined &&
      stepStates['listing'].postalCode.length > POSTALCODE_LENGTH &&
      stepStates['listing'].price !== null &&
      stepStates['listing'].price !== undefined &&
      stepStates['listing'].price >= MIN_PRICE

    if (isValid && stepStates['listing'].types[0] !== 'Casa') {
      isValid =
        stepStates['listing'].complement !== undefined &&
        stepStates['listing'].complement &&
        stepStates['listing'].complement.length >= MIN_COMPLEMENT_LENGTH
    }
    return isValid
  }

  const isStepContactValid = () => {
    const {name, phone, email} = stepStates['contact']
    let isValid =
      name !== undefined &&
      phone !== undefined &&
      phone &&
      phone.startsWith('+55') &&
      phone.length === PHONE_LENGTH &&
      email !== undefined &&
      EMAIL_REGEX.test(email)
    return isValid
  }

  const isBackButtonDisabled = () => !(steps[step] && steps[step].stepBack)
  const isFwdButtonDisabled = () => {
    if (step === 'listing') {
      return !isStepListingValid()
    } else if (step === 'contact') {
      return !isStepContactValid()
    } else {
      return !(steps[step] && steps[step].stepFwd)
    }
  }

  const getLogStates = () => {
    return {
      ...stepStates,
      step
    }
  }

  const successButtonUrl = useMemo(() => {
    let url = '/imoveis/sp/sao-paulo/ideal-para-voce'
    if (stepStates.listing.addressData) {
      const address = getAddressInput({
        addressData: stepStates.listing.addressData
      })

      if (address?.city && address?.state) {
        url = `/imoveis/${slugify(address.state).toLowerCase()}/${slugify(
          address.city
        ).toLowerCase()}/ideal-para-voce`
      }
    }
    return url
  }, [stepStates.listing.addressData])

  function handleLastFwdClick() {
    if (userProfile && userProfile.id) {
      changeStep('send')
    } else {
      const phone = (stepStates && stepStates['contact'].phone) || null
      dispatch(showLoginModal({show: true, phone: phone}))
    }
  }

  function onSubmit() {
    const addressData = getAddressInput({
      addressData: stepStates.listing.addressData
    })
    const input = {
      lead: {
        type: stepStates.listing.types[0],
        complement: stepStates.listing.complement,
        price: stepStates.listing.price
      },
      address: {
        ...addressData,
        streetNumber: stepStates.listing.streetNumber,
        postalCode: stepStates.listing.postalCode
      },
      contact: {
        name: stepStates.contact.name,
        email: stepStates.contact.email
      },
      utm: getUTMObject()
    }
    setInput(input)
  }

  function onSubmitSteps() {
    if (!isFwdButtonDisabled()) {
      logger.action('seller-onboarding-onsubmitform', getLogStates())
      logger.action('seller-onboarding-step-forward', getLogStates())
      step === 'contact'
        ? handleLastFwdClick()
        : changeStep(steps[step].stepFwd)
    }
  }

  useEffect(() => {
    if (called && !error && !data) {
      logger.action('seller-onboarding-step-send', {input, userProfile})
    } else if (called && data) {
      const {
        simplifiedSiteSellerLeadCreate: {addressIsCovered}
      } = data
      if (addressIsCovered) {
        logger.action('seller-onboarding-step-send-success', {
          input,
          userProfile
        })
        if (userProfile && (userProfile.phone || userProfile.email)) {
          const user = getSha256Data(userProfile)
          logger.action(loggerAction.success, user)
        } else {
          logger.action(loggerAction.success)
        }
        changeStep('success')
      } else {
        logger.action('seller-onboarding-step-send-addressIsNotCovered', {
          input,
          userProfile
        })
        changeStep('success')
      }
    } else if (error) {
      logger.capture(error)
      logger.action('seller-onboarding-step-send-error', {
        error,
        input,
        userProfile
      })
      changeStep('error')
    }
  }, [called, error, data])

  useEffect(() => {
    if (input) {
      simplifiedSiteSellerLeadCreate({
        variables: {input}
      })
    }
  }, [input])

  useEffect(() => {
    if (step === 'send') {
      onSubmit()
    } else if (step === 'success') {
      const addressIsCovered =
        data && data.simplifiedSiteSellerLeadCreate.addressIsCovered
      const checkQuintoAndarDuplicity =
        data?.simplifiedSiteSellerLeadCreate?.checkQuintoAndarDuplicity

      logger.action('seller-onboarding-step-success', {addressIsCovered})

      if (checkQuintoAndarDuplicity) {
        logger.action('seller-onboarding-duplicated-quintoandar', {
          input,
          userProfile
        })
        changeResult({
          large: true,
          illustrationTiny: true,
          illustration: `${process.env.CLOUDINARY_IMAGE_URL}/site/quintoandar/parceria-emcasa-quintoandar`,
          title:
            'Seu imóvel está anunciado para vender no QuintoAndar, por isso já está na EmCasa',
          text: (
            <>
              Identificamos que seu imóvel já está anunciado no QuintoAndar.
              Nossos corretores já estão trabalhando seu imóvel e oferecendo
              para nossos compradores. <br />
              Com isso não existe a necessidade de você anunciar novamente aqui
              na EmCasa.
            </>
          ),
          buttons: [
            {
              active: true,
              label: 'Tentar outro endereço',
              onClick: () => {
                logger.action(
                  'seller-onboarding-duplicated-quintoandar-button',
                  {
                    input,
                    userProfile
                  }
                )
                history.go(0)
              }
            }
          ]
        })
      } else {
        if (addressIsCovered) {
          changeResult({
            success: true,
            icon: '/img/illustrations/check.svg',
            iconFull: true,
            title: 'Imóvel cadastrado',
            text:
              'Vamos entrar em contato por WhatsApp ou ligação para coletar as demais informações e agendar a sessão de fotos gratuita.',
            buttons: [
              {
                label: 'Cadastrar outro imóvel',
                onClick: () => history.go(0)
              }
            ]
          })
        } else {
          changeResult({
            error: true,
            icon: '/img/icons/location.svg',
            title: 'Região não atendida :(',
            text:
              'Ainda não atendemos essa região. Avisamos assim que expandirmos',
            buttons: [
              {
                active: true,
                label: 'Ir para página inicial',
                to: '/'
              },
              {
                label: 'Cadastrar outro imóvel',
                onClick: () => history.go(0)
              }
            ]
          })
        }
      }
    } else if (step === 'error') {
      logger.action('seller-onboarding-step-error')
      changeResult({
        error: true,
        icon: '/img/icons/exclamation-mark.svg',
        title: 'Ocorreu um erro',
        text: 'Tente enviar os dados novamente',
        buttons: [
          {
            active: true,
            label: 'Tentar novamente',
            onClick: () => changeStep('listing')
          }
        ]
      })
    } else if (step === 'listing') {
      logger.action('seller-onboarding-step-listing')
    } else if (step === 'contact') {
      logger.action('seller-onboarding-step-contact')
    }
  }, [step])

  useEffect(() => {
    if (userProfile && userProfile.id) {
      changeStepStates({
        ...stepStates,
        contact: {
          name: userProfile.name || stepStates.contact.name,
          phone: userProfile.phone || stepStates.contact.phone,
          email: userProfile.email || stepStates.contact.email
        }
      })
    }
  }, [userProfile])

  useEffect(() => {
    logger.action('seller-onboarding-page', {
      evaluation: !!stepStates.listing.address
    })
  }, [])

  return (
    <Shell noFooter>
      <HeadHelmet
        title="Venda seu imóvel com praticidade e segurança com a EmCasa."
        description="Vender um Imóvel é simples. Anuncie agora com a EmCasa e receba fotos profissionais, divulgação gratuita, visitas relevantes e assessoria jurídica. Conheça!"
        url={'https://emcasa.com/vender-imovel'}
        image={`${process.env.CLOUDINARY_IMAGE_URL}/share/share-2`}
      />
      <div
        className={classNames({
          [styles.ecStepByStep__seller]: step === 'success'
        })}
      >
        <StepByStep
          steps={steps}
          step={step}
          result={result}
          navigateToStep={changeStep}
          backButtonLabel="Voltar"
          fwdButtonLabel={step === 'contact' ? 'Enviar' : 'Avançar'}
          isBackButtonDisabled={isBackButtonDisabled()}
          isFwdButtonDisabled={isFwdButtonDisabled()}
          onBackButtonClick={() =>
            logger.action('seller-onboarding-step-button-back', getLogStates())
          }
          onFwdButtonClick={() => {
            logger.action('seller-onboarding-step-button-next', getLogStates())
            logger.action('seller-onboarding-step-forward', getLogStates())
          }}
          onHandleLastFwdClick={handleLastFwdClick}
        />
        {step === 'success' && (
          <div className={styles.ecStepByStep__sellerBanner}>
            <Banner
              greyBox
              icon="/img/icons/search-grey900.svg"
              title="Pretende comprar um novo imóvel?"
              text="Está vendendo seu imóvel para comprar um novo? Conte o que você está procurando e enviaremos opções ideais para o seu perfil via email e WhatsApp"
              button={{
                label: 'Quero receber imóveis',
                href: successButtonUrl,
                onClick: () =>
                  logger.action(
                    'seller-onboarding-step-success-qev-button',
                    getLogStates()
                  )
              }}
            />
          </div>
        )}
      </div>
    </Shell>
  )
}

export default compose(withUserProfile, withRestoredScroll)(SellerCreatePage)
